//@ts-check
/**
 * How far off a list's date is, said the way you would say it about something you are looking
 * forward to: roughly when it is far away, exactly when it is close (guides/WEBSITE_UI_CODING_GUIDE.md,
 * "A list's date is something to look forward to"). A precise "93 days" reads like a deadline.
 *
 * Plain JavaScript, no runes, so it is unit-tested directly.
 */

/**
 * Whole days from today to a date, on the viewer's own calendar. Both are taken at local midnight
 * and the difference rounded, so a daylight-saving change in between does not shift it by one.
 *
 * @param {string} date YYYY-MM-DD
 * @param {Date} [now]
 */
export function daysUntil(date, now = new Date()) {
  const [y, m, d] = date.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Which phrase to use, and the number that goes in it. Null once the date has passed: the
 * countdown simply goes away, with nothing "overdue" in its place.
 *
 * @param {number} days from `daysUntil`
 * @returns {{ key: "today" | "tomorrow" | "days" | "weeks" | "months" | "years", count: number } | null}
 */
export function countdownPhrase(days) {
  if (days < 0) return null;
  if (days === 0) return { key: "today", count: 0 };
  if (days === 1) return { key: "tomorrow", count: 1 };
  if (days < 14) return { key: "days", count: days };
  if (days < 60) return { key: "weeks", count: Math.round(days / 7) };
  if (days < 365) return { key: "months", count: Math.round(days / 30.44) };
  return { key: "years", count: Math.max(1, Math.round(days / 365.25)) };
}

/**
 * The list with the nearest date still to come, or null. Only the nearest is shown: a stack of
 * countdowns turns into a planner.
 *
 * @template {{ date: string | null }} L
 * @param {L[]} lists
 * @param {Date} [now]
 * @returns {{ list: L, days: number } | null}
 */
export function nearestDated(lists, now = new Date()) {
  /** @type {{ list: L, days: number } | null} */
  let nearest = null;
  for (const list of lists) {
    if (!list.date) continue;
    const days = daysUntil(list.date, now);
    if (days >= 0 && (!nearest || days < nearest.days)) nearest = { list, days };
  }
  return nearest;
}
